// SearchTuningSection.tsx — CTSK-504. "Search tuning (advanced)" under
// Settings → Search (moved from Documents in CTSK-523). Admin only (settings:manage). Collapsed by default.
//
// The nine keys are clamped server-side on read AND write by the shared
// normaliser; this form mirrors the same clamps (imported, not copied) so
// what the admin sees is what gets saved. Defaults reproduce today's search
// behaviour exactly; "Reset to defaults" restores them in the draft (the
// page's Save button persists).

import {
  NEIGHBORHOOD_MAX_PER_BASIS_MAX,
  NEIGHBORHOOD_MAX_PER_BASIS_MIN,
  SEARCH_HEADLINE_CHARS_MAX,
  SEARCH_LABEL_BOOST_MAX,
  SEARCH_LABEL_BOOST_MIN,
  SEARCH_HEADLINE_CHARS_MIN,
  SEARCH_SLOW_MS_MAX,
  SEARCH_SLOW_MS_MIN,
  SEARCH_STATEMENT_TIMEOUT_MS_MAX,
  SEARCH_STATEMENT_TIMEOUT_MS_MIN,
  SEARCH_TUNING_DEFAULTS,
  normalizeSearchLabelBoost,
  normalizeSearchRankWeights,
  normalizeSearchTuningSettings,
} from '@captain-tasko/shared';

const WEIGHT_LABELS = ['D · other', 'C · nest & doc type', 'B · content & tags', 'A · title'] as const;
const inputStyle = { width: '100%', padding: 6, border: '1px solid var(--border-strong)', borderRadius: 4 } as const;

interface Props {
  values: Record<string, unknown>;
  onChange: (k: string, v: unknown) => void;
  /** settings:manage. When false nothing renders. */
  canEdit: boolean;
  /** CTSK-523 — open the disclosure by default (the Search tab is its own page). */
  defaultOpen?: boolean;
}

function Row({ label, hint, children }: { label: string; hint: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>{label}</div>
      {children}
      <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }}>{hint}</div>
    </div>
  );
}

export function SearchTuningSection({ values, onChange, canEdit, defaultOpen = false }: Props) {
  if (!canEdit) return null;
  const t = normalizeSearchTuningSettings(values);
  const weights = normalizeSearchRankWeights(values.searchRankWeights);

  function setNumber(k: string, raw: string, min: number, max: number, fallback: number) {
    if (raw === '') return onChange(k, fallback);
    const n = Number(raw);
    onChange(k, Number.isFinite(n) ? Math.min(max, Math.max(min, Math.trunc(n))) : fallback);
  }

  return (
    <details
      data-testid="search-tuning"
      open={defaultOpen || undefined}
      style={{ margin: '16px 0', padding: '8px 12px', border: '1px solid var(--border-strong)', borderRadius: 6 }}
    >
      <summary style={{ cursor: 'pointer', fontWeight: 600 }}>Search tuning (advanced)</summary>
      <div style={{ marginTop: 12 }}>
        <p style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 0 }}>
          These change how document search runs, with no re-index. The defaults reproduce the built-in behaviour exactly.
        </p>

        <Row
          label="Ranking weights (title / content / nest)"
          hint={`Each 0–1. Default ${SEARCH_TUNING_DEFAULTS.searchRankWeights.join(' / ')} (D, C, B, A). Raise B to favour body matches over titles.`}
        >
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
            {WEIGHT_LABELS.map((lab, i) => (
              <label key={lab} style={{ fontSize: 12 }}>
                {lab}
                <input
                  type="number"
                  step={0.05}
                  min={0}
                  max={1}
                  aria-label={`Rank weight ${lab}`}
                  value={weights[i]}
                  onChange={(e) => {
                    const next = [...weights];
                    next[i] = e.target.value === '' ? SEARCH_TUNING_DEFAULTS.searchRankWeights[i]! : Number(e.target.value);
                    onChange('searchRankWeights', normalizeSearchRankWeights(next));
                  }}
                  style={inputStyle}
                />
              </label>
            ))}
          </div>
        </Row>

        <Row
          label="Label weight (group, collection, folder)"
          hint={`${SEARCH_LABEL_BOOST_MIN}–${SEARCH_LABEL_BOOST_MAX}, default ${SEARCH_TUNING_DEFAULTS.searchLabelBoost}. Only applies while document grouping is on. 0 = labels still match but add nothing to the ranking; higher ranks label matches above body matches.`}
        >
          <input
            type="number"
            step={0.1}
            min={SEARCH_LABEL_BOOST_MIN}
            max={SEARCH_LABEL_BOOST_MAX}
            aria-label="Label weight"
            value={t.searchLabelBoost}
            onChange={(e) =>
              onChange(
                'searchLabelBoost',
                e.target.value === '' ? SEARCH_TUNING_DEFAULTS.searchLabelBoost : normalizeSearchLabelBoost(e.target.value),
              )
            }
            style={inputStyle}
          />
        </Row>

        <div style={{ marginBottom: 12 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
            <input
              type="checkbox"
              checked={t.searchFacetsEnabled}
              onChange={(e) => onChange('searchFacetsEnabled', e.target.checked)}
            />
            Show group, collection and folder counts on search results
          </label>
          <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }}>
            Default on. Only applies while document grouping is on. Turn off if the facet step dominates search time (see Admin → Search diagnostics).
          </div>
        </div>

        <Row
          label="Related documents per basis (neighborhood)"
          hint={`${NEIGHBORHOOD_MAX_PER_BASIS_MIN}–${NEIGHBORHOOD_MAX_PER_BASIS_MAX}, default ${SEARCH_TUNING_DEFAULTS.neighborhoodMaxPerBasis}. How many related documents the document panel lists per shared batch, folder, group or collection when the request names no limit.`}
        >
          <input
            type="number"
            aria-label="Related documents per basis"
            value={t.neighborhoodMaxPerBasis}
            onChange={(e) => setNumber('neighborhoodMaxPerBasis', e.target.value, NEIGHBORHOOD_MAX_PER_BASIS_MIN, NEIGHBORHOOD_MAX_PER_BASIS_MAX, SEARCH_TUNING_DEFAULTS.neighborhoodMaxPerBasis)}
            style={inputStyle}
          />
        </Row>

        <div style={{ marginBottom: 12 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
            <input
              type="checkbox"
              checked={t.searchTrgmFallback}
              onChange={(e) => onChange('searchTrgmFallback', e.target.checked)}
            />
            Substring (trigram) fallback on titles
          </label>
          <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }}>
            Default on. Turn off to match whole words only (cheaper on very large libraries).
          </div>
        </div>

        <Row
          label="Snippet window (characters of content scanned)"
          hint={`${SEARCH_HEADLINE_CHARS_MIN.toLocaleString()}–${SEARCH_HEADLINE_CHARS_MAX.toLocaleString()}, default ${SEARCH_TUNING_DEFAULTS.searchHeadlineChars.toLocaleString()}. Lower it if snippet generation dominates search time.`}
        >
          <input
            type="number"
            aria-label="Snippet window"
            value={t.searchHeadlineChars}
            onChange={(e) => setNumber('searchHeadlineChars', e.target.value, SEARCH_HEADLINE_CHARS_MIN, SEARCH_HEADLINE_CHARS_MAX, SEARCH_TUNING_DEFAULTS.searchHeadlineChars)}
            style={inputStyle}
          />
        </Row>

        <Row
          label="Search timeout (ms)"
          hint={`${SEARCH_STATEMENT_TIMEOUT_MS_MIN}–${SEARCH_STATEMENT_TIMEOUT_MS_MAX.toLocaleString()}, default ${SEARCH_TUNING_DEFAULTS.searchStatementTimeoutMs.toLocaleString()}. A slower search is cancelled and returns "search timed out".`}
        >
          <input
            type="number"
            aria-label="Search timeout"
            value={t.searchStatementTimeoutMs}
            onChange={(e) => setNumber('searchStatementTimeoutMs', e.target.value, SEARCH_STATEMENT_TIMEOUT_MS_MIN, SEARCH_STATEMENT_TIMEOUT_MS_MAX, SEARCH_TUNING_DEFAULTS.searchStatementTimeoutMs)}
            style={inputStyle}
          />
        </Row>

        <Row
          label="Slow-search threshold (ms)"
          hint={`${SEARCH_SLOW_MS_MIN}–${SEARCH_SLOW_MS_MAX.toLocaleString()}, default ${SEARCH_TUNING_DEFAULTS.searchSlowMs}. Searches above this are logged as warnings.`}
        >
          <input
            type="number"
            aria-label="Slow-search threshold"
            value={t.searchSlowMs}
            onChange={(e) => setNumber('searchSlowMs', e.target.value, SEARCH_SLOW_MS_MIN, SEARCH_SLOW_MS_MAX, SEARCH_TUNING_DEFAULTS.searchSlowMs)}
            style={inputStyle}
          />
        </Row>

        <div style={{ marginBottom: 12 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
            <input
              type="checkbox"
              checked={t.searchLogQueries}
              onChange={(e) => onChange('searchLogQueries', e.target.checked)}
            />
            Include search text in server logs
          </label>
          <div role="note" style={{ fontSize: 12, color: 'var(--warning-primary)', marginTop: 4 }}>
            Off by default. Queries can contain personal data; turning this on writes the first 200 characters of what people search for into the server log.
          </div>
        </div>

        <button
          type="button"
          className="btn"
          onClick={() => {
            for (const [k, v] of Object.entries(SEARCH_TUNING_DEFAULTS)) onChange(k, Array.isArray(v) ? [...v] : v);
          }}
        >
          Reset search tuning to defaults
        </button>
      </div>
    </details>
  );
}
